import { useCallback, useRef, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import Image from 'components/Common/Image'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import IconButton from 'LuxKit/Button/IconButton'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import CruiseV2SavedPriceDetails from 'tripPlanner/components/ItemOfferPrice/CruiseV2SavedPriceDetails'
import type { LeCruiseSavedItemData } from 'tripPlanner/api/types'
import { getTransportIcon } from './getTransportIcon'

const CRUISE_IMAGE_WIDTH = 180
const CRUISE_IMAGE_HEIGHT = 116
const DELETE_START_ICON = <LineTrashAltIcon size="S" />

const OfferCard = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  width: 100%;
  min-height: ${rem(116)};
  overflow: hidden;
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.L};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};

  @container chat-itinerary-canvas (min-width: ${rem(560)}) {
    grid-template-columns: var(--itinerary-item-image-width, ${rem(179)}) minmax(0, 1fr);
  }
`

const ImageArea = styled.div`
  min-height: ${rem(116)};
  overflow: hidden;
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const StyledImage = styled(Image)`
  width: 100%;
  height: 100%;
`

const ImageFallback = styled.div`
  width: 100%;
  height: 100%;
  min-height: ${rem(116)};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  font-size: ${rem(24)};
`

const OfferContent = styled.div`
  min-width: 0;
  padding: ${rem(16)} ${rem(20)};
`

const OfferHeader = styled(Group)`
  min-width: 0;
`

const OfferTitle = styled(Group)`
  min-width: 0;
`

const PriceActions = styled(Group)`
  min-height: ${rem(38)};
`

const ActionButtons = styled(Group)`
  flex-shrink: 0;
`

const PriceDetailsWrapper = styled.div`
  min-width: 0;
`

interface Props {
  savedItemData: LeCruiseSavedItemData
  transportType: string
  shipName: string
  detailLabel: string
  deleteLabel: string
  viewOfferLabel: string
  deleteActionsDisabled: boolean
  onDelete?: () => void
  onViewOffer: () => void
}

function ChatCruiseTransportOfferCard({
  savedItemData,
  transportType,
  shipName,
  detailLabel,
  deleteLabel,
  viewOfferLabel,
  deleteActionsDisabled,
  onDelete,
  onViewOffer,
}: Props) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuTriggerRef = useRef<HTMLButtonElement>(null)
  const cruiseImageId = savedItemData.imageIds?.[0]
  const hasPriceDetails = savedItemData.price != null || savedItemData.isOfferSoldOut
  const closeMenu = useCallback(() => setIsMenuOpen(false), [])
  const toggleMenu = useCallback(() => {
    if (deleteActionsDisabled) return
    setIsMenuOpen(prev => !prev)
  }, [deleteActionsDisabled])
  const handleDelete = useCallback(() => {
    if (deleteActionsDisabled || !onDelete) return
    closeMenu()
    onDelete()
  }, [closeMenu, deleteActionsDisabled, onDelete])

  const cruiseImage = cruiseImageId ? (
    <StyledImage
      id={cruiseImageId}
      alt=""
      width={CRUISE_IMAGE_WIDTH}
      height={CRUISE_IMAGE_HEIGHT}
      fit="center"
      objectFit="cover"
      dpr={2}
    />
  ) : (
    <ImageFallback aria-hidden>
      {getTransportIcon(transportType, 'M')}
    </ImageFallback>
  )
  const deleteMenu = onDelete ? (
    <>
      <IconButton
        kind="tertiary"
        size="small"
        ref={menuTriggerRef}
        onClick={toggleMenu}
        disabled={deleteActionsDisabled}
      >
        <LineEllipsisVIcon />
      </IconButton>
      <DropdownList
        size="S"
        open={isMenuOpen}
        onClose={closeMenu}
        triggerRef={menuTriggerRef}
        anchorRef={menuTriggerRef}
        placement="bottom-end"
        portaled
      >
        <ClickableListItem
          title={deleteLabel}
          size="medium"
          startIcon={DELETE_START_ICON}
          onClick={handleDelete}
        />
      </DropdownList>
    </>
  ) : null

  return (
    <OfferCard>
      <ImageArea>
        {cruiseImage}
      </ImageArea>
      <OfferContent>
        <Group direction="vertical" gap={8}>
          <OfferHeader direction="horizontal" gap={8} horizontalAlign="space-between" verticalAlign="start">
            <OfferTitle direction="vertical" gap={4}>
              <BodyText variant="medium" weight="bold">{shipName}</BodyText>
              <BodyText variant="small" colour="neutral-two">{detailLabel}</BodyText>
            </OfferTitle>
            {deleteMenu}
          </OfferHeader>
          <PriceActions direction="horizontal" gap={12} horizontalAlign="space-between" verticalAlign="end">
            {hasPriceDetails && (
              <PriceDetailsWrapper>
                <CruiseV2SavedPriceDetails
                  price={savedItemData.price}
                  currencyCode={savedItemData.currencyCode}
                  value={savedItemData.value}
                  cabinCategory={savedItemData.cabinCategory}
                  isOfferAvailable={!savedItemData.isOfferSoldOut}
                />
              </PriceDetailsWrapper>
            )}
            <ActionButtons direction="horizontal" gap={8}>
              <TextButton kind="primary" size="small" onClick={onViewOffer} disabled={!savedItemData.offerId}>
                {viewOfferLabel}
              </TextButton>
            </ActionButtons>
          </PriceActions>
        </Group>
      </OfferContent>
    </OfferCard>
  )
}

export default ChatCruiseTransportOfferCard
